import { useMemo } from 'react';

import { DAILY_MAX_DOMAIN_EU_STOCKS, DAILY_MAX_DOMAIN_US_STOCKS } from '../../lib/store/market-data/charting/constants';
import SmallChartDataCache from '../../lib/store-util/SmallChartDataCache';
import TradePriceCache from '../../lib/store-util/TradePriceCache';
import { getChartLineColorByLineClose } from '../../lib/util/ChartingHelpers';
import { getSymbolFirstPart } from '../../lib/util/MarketDataHelpers';

import useIsMobile from '../hooks/useIsMobile';

import { LINE_CHART_HEIGHT } from './constants';
import { getSmallChartMaxHeight, getSmallChartPaddingRatio, getSmallChartStrokeWidth } from './helpers';
import SparklineCanvas from './SparklineCanvas';

type OwnProps = {
  symbol: string;
  isChartInCard?: boolean;
  isSmallGraph?: boolean;
  isEuroStock?: boolean;
};

type Props = OwnProps;

const ChartLine = (props: Props) => {
  const { symbol: symbolRaw, isChartInCard, isSmallGraph, isEuroStock } = props;
  const symbol = getSymbolFirstPart(symbolRaw);
  const { isMobile } = useIsMobile();

  const maxChartHeight = isSmallGraph && isMobile
    ? LINE_CHART_HEIGHT : getSmallChartMaxHeight(Boolean(isChartInCard), isMobile);

  const { closeValues, isReady, dailyMaxPoints } = SmallChartDataCache.use(symbol);

  const tradePrice = TradePriceCache.use(symbol, 'ChartLine');
  const lineClose = tradePrice?.priceData.lineClose;

  const paddingRatio = useMemo(
    () => getSmallChartPaddingRatio(Boolean(isChartInCard), isMobile),
    [ isChartInCard, isMobile ],
  );

  const lastCloseValue = closeValues.length > 0 ? closeValues[closeValues.length - 1] : undefined;
  const firstCloseValue = closeValues.length > 0 ? closeValues[0] : undefined;
  const hasNoData = !isReady || closeValues.length === 0;
  const referenceValue = lineClose ?? firstCloseValue;
  const stroke = getChartLineColorByLineClose(referenceValue, lastCloseValue);

  if (hasNoData) {
    return <span style={{ color: stroke }} />;
  }

  return (
    <SparklineCanvas
      values={closeValues}
      referenceValue={referenceValue}
      color={stroke}
      designHeight={maxChartHeight}
      maxPoints={dailyMaxPoints ?? (isEuroStock ? DAILY_MAX_DOMAIN_EU_STOCKS : DAILY_MAX_DOMAIN_US_STOCKS)}
      paddingRatio={paddingRatio}
      strokeWidth={getSmallChartStrokeWidth(isMobile)}
      withGradient={!isChartInCard}
    />
  );
};

export default ChartLine;
